import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../../components/ui/button';
import { CdaLogo } from '../../../components/cda/CdaBrand';
import { LanguageBar } from '../../../i18n/cda';
import { CheckCircle, ArrowRight, Clock, Building, FileCheck, Users } from 'lucide-react';

const Cda480HoursSupport = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#fbf8f3]">
      <Helmet>
        <title>CDA 480 Hours Experience Support | How to Complete Your CDA Hours</title>
        <meta name="description" content="Need help completing your 480 hours of CDA experience? Care Comply connects you with childcare centers and guides you through tracking your required work hours for the CDA credential." />
        <link rel="canonical" href="https://carecomplycda.com/cda-480-hours-support" />
      </Helmet>

      <LanguageBar />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/cda"><CdaLogo size={34} /></Link>
          <Button onClick={() => navigate('/cda')} className="bg-[#0d7d6f] hover:bg-[#0a6358]">
            Start Your Journey
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-12">
        <div className="text-center mb-12">
          <p className="text-[#c4622d] font-semibold mb-2">CDA Experience Requirement</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            How to Complete Your 480 CDA Hours
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            The 480-hour experience requirement is one of the biggest challenges CDA candidates face. Care Comply helps you find placement and track your hours.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Understanding the 480-Hour Requirement</h2>
          <p className="text-slate-600 mb-4">
            To earn your Child Development Associate (CDA) credential, you must complete at least <strong>480 hours of professional experience</strong> working with children in your chosen age group (Infant/Toddler, Preschool, or Family Child Care) within the past three years.
          </p>
          <p className="text-slate-600 mb-4">
            This experience must be in a state-approved childcare setting and can include paid employment, student teaching, or volunteer work — as long as you're directly working with children.
          </p>
          
          <div className="bg-[#fbf8f3] rounded-xl p-5 mt-6">
            <h3 className="font-semibold text-slate-900 mb-3">What Counts Toward Your 480 Hours:</h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" /> Working as a teacher or assistant in a licensed childcare center</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" /> Student teaching placements</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" /> Volunteering at a childcare program (with proper documentation)</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" /> Working in Head Start or Early Head Start programs</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" /> Family child care provider experience</li>
            </ul>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Building className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Center Placement Help</h3>
            <p className="text-slate-600">Care Comply partners with childcare centers in Texas to help you find a placement to earn your hours.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Clock className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Hour Tracking Tools</h3>
            <p className="text-slate-600">Use our tracking system to log your hours and ensure you meet the 480-hour requirement.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <FileCheck className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Documentation Support</h3>
            <p className="text-slate-600">Get help with the paperwork needed to verify your experience for the CDA Council.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Users className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Mentor Matching</h3>
            <p className="text-slate-600">Connect with experienced educators who can guide you through your classroom experience.</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-slate-900">Can I count hours from multiple jobs?</h3>
              <p className="text-slate-600">Yes! You can combine hours from different childcare settings, as long as all experience is within the past 3 years and documented properly.</p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">What if I don't currently work at a childcare center?</h3>
              <p className="text-slate-600">Care Comply can help connect you with partner centers in Texas that accept CDA candidates for paid positions or volunteer placements.</p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">How do I document my hours?</h3>
              <p className="text-slate-600">You'll need a signed letter from your employer or supervisor verifying your hours worked. We provide templates and guidance to make this easy.</p>
            </div>
          </div>
        </div>

        <div className="bg-[#0d7d6f] rounded-2xl p-8 text-white text-center mb-8">
          <h2 className="text-2xl font-bold mb-3">Need Help With Your 480 Hours?</h2>
          <p className="mb-6 opacity-90">Care Comply connects you with centers and tracks your progress every step of the way.</p>
          <Button onClick={() => navigate('/cda')} className="bg-white text-[#0d7d6f] hover:bg-slate-100">
            Get Started Today <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="text-center text-sm text-slate-500">
          <p>Care Comply serves San Antonio, Austin, Houston, Dallas, and all of Texas.</p>
          <p className="mt-2">
            <Link to="/cda-career-coaching-san-antonio" className="text-[#0d7d6f] hover:underline">Career Coaching</Link> • 
            <Link to="/cda-portfolio-help" className="text-[#0d7d6f] hover:underline ml-2">Portfolio Help</Link> • 
            <Link to="/cda-job-readiness" className="text-[#0d7d6f] hover:underline ml-2">Job Readiness</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default Cda480HoursSupport;
